/**
 * Divider Component
 *
 * A semantic separator component that creates visual separation
 * between content sections. Supports both horizontal and vertical
 * orientations with configurable spacing and colors.
 *
 * Uses CSS custom properties (design tokens) for spacing and colors,
 * ensuring consistency across the design system.
 *
 * @module components/core/Divider
 *
 * @example
 * ```tsx
 * // Simple horizontal divider
 * <VStack>
 *   <Section1 />
 *   <Divider />
 *   <Section2 />
 * </VStack>
 *
 * // Divider with spacing
 * <VStack>
 *   <Content />
 *   <Divider spacing="lg" />
 *   <MoreContent />
 * </VStack>
 *
 * // Vertical divider in horizontal layout
 * <HStack>
 *   <Column1 />
 *   <Divider orientation="vertical" />
 *   <Column2 />
 * </HStack>
 *
 * // Divider with label
 * <VStack>
 *   <LoginForm />
 *   <Divider label="or" />
 *   <SocialLoginButtons />
 * </VStack>
 * ```
 */

import { type CSSProperties, type ReactNode, forwardRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * Divider orientation options
 */
export type DividerOrientation = 'horizontal' | 'vertical';

/**
 * Divider spacing options
 */
export type DividerSpacing = 'none' | 'sm' | 'md' | 'lg';

/**
 * Props for the Divider component
 */
export interface DividerProps {
  /**
   * Orientation of the divider
   * @default 'horizontal'
   */
  orientation?: DividerOrientation;

  /**
   * Spacing (margin) around the divider
   * - none: 0
   * - sm: var(--spacing-sm)
   * - md: var(--spacing-md)
   * - lg: var(--spacing-lg)
   * @default 'none'
   */
  spacing?: DividerSpacing;

  /**
   * Color of the divider line
   * Can be a CSS color value or a CSS variable
   * @default 'var(--color-border)'
   */
  color?: string;

  /**
   * Thickness of the divider line
   * @default '1px'
   */
  thickness?: string;

  /**
   * Optional label to display in the center of the divider
   */
  label?: ReactNode;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Inline styles (use sparingly - prefer tokens)
   */
  style?: CSSProperties;

  /**
   * Whether the divider should be decorative (aria-hidden)
   * Set to false if the divider conveys meaningful separation
   * @default true
   */
  decorative?: boolean;

  /**
   * Optional data-testid for testing
   */
  'data-testid'?: string;
}

/**
 * Map spacing values to CSS custom property values
 */
const spacingMap: Record<DividerSpacing, string> = {
  none: '0',
  sm: 'var(--spacing-sm)',
  md: 'var(--spacing-md)',
  lg: 'var(--spacing-lg)',
};

/**
 * Default divider color using CSS variable
 * Fallback defined in design-variables.css
 */
const DEFAULT_COLOR = 'var(--color-border)';

/**
 * Divider Component
 *
 * Creates visual separation between content sections using design system tokens.
 * Supports labels for "or" dividers and both horizontal/vertical orientations.
 */
export const Divider = forwardRef<HTMLDivElement, DividerProps>(function Divider(
  {
    orientation = 'horizontal',
    spacing = 'none',
    color = DEFAULT_COLOR,
    thickness = '1px',
    label,
    className,
    style,
    decorative = true,
    'data-testid': testId,
    ...restProps
  },
  ref
) {
  const isHorizontal = orientation === 'horizontal';
  const hasLabel = label !== undefined && label !== null;

  // Build styles based on orientation
  const dividerStyle: CSSProperties = {
    ...(isHorizontal
      ? {
          marginTop: spacingMap[spacing],
          marginBottom: spacingMap[spacing],
        }
      : {
          marginLeft: spacingMap[spacing],
          marginRight: spacingMap[spacing],
        }),
    ...style,
  };

  // Line styles
  const lineStyle: CSSProperties = {
    backgroundColor: color,
    ...(isHorizontal ? { height: thickness, width: '100%' } : { width: thickness, height: '100%' }),
  };

  // If there's a label, render a more complex structure
  if (hasLabel && isHorizontal) {
    return (
      <div
        ref={ref}
        className={cn('flex items-center w-full', className)}
        style={dividerStyle}
        role={decorative ? 'none' : 'separator'}
        aria-hidden={decorative}
        aria-orientation={orientation}
        data-testid={testId}
        {...restProps}
      >
        {/* Left line */}
        <div className="flex-grow" style={lineStyle} />

        {/* Label */}
        <span
          className="flex-shrink-0 px-3 text-sm text-muted-foreground"
          style={{
            paddingLeft: 'var(--spacing-md)',
            paddingRight: 'var(--spacing-md)',
          }}
        >
          {label}
        </span>

        {/* Right line */}
        <div className="flex-grow" style={lineStyle} />
      </div>
    );
  }

  // Simple divider without label
  return (
    <div
      ref={ref}
      className={cn(isHorizontal ? 'w-full' : 'h-full self-stretch', className)}
      style={{
        ...dividerStyle,
        ...lineStyle,
      }}
      role={decorative ? 'none' : 'separator'}
      aria-hidden={decorative}
      aria-orientation={orientation}
      data-testid={testId}
      {...restProps}
    />
  );
});

Divider.displayName = 'Divider';

/**
 * HDivider Component
 *
 * A convenience component for horizontal dividers.
 *
 * @example
 * ```tsx
 * <VStack>
 *   <Section1 />
 *   <HDivider spacing="md" />
 *   <Section2 />
 * </VStack>
 * ```
 */
export const HDivider = forwardRef<HTMLDivElement, Omit<DividerProps, 'orientation'>>(function HDivider(props, ref) {
  return <Divider ref={ref} orientation="horizontal" {...props} />;
});

HDivider.displayName = 'HDivider';

/**
 * VDivider Component
 *
 * A convenience component for vertical dividers.
 *
 * @example
 * ```tsx
 * <HStack>
 *   <Column1 />
 *   <VDivider spacing="sm" />
 *   <Column2 />
 * </HStack>
 * ```
 */
export const VDivider = forwardRef<HTMLDivElement, Omit<DividerProps, 'orientation'>>(function VDivider(props, ref) {
  return <Divider ref={ref} orientation="vertical" {...props} />;
});

VDivider.displayName = 'VDivider';

/**
 * OrDivider Component
 *
 * A convenience component for "or" dividers commonly used in auth flows.
 *
 * @example
 * ```tsx
 * <VStack>
 *   <EmailLoginForm />
 *   <OrDivider />
 *   <GoogleLoginButton />
 * </VStack>
 * ```
 */
export const OrDivider = forwardRef<HTMLDivElement, Omit<DividerProps, 'orientation' | 'label'>>(
  function OrDivider(props, ref) {
    return <Divider ref={ref} orientation="horizontal" label="or" spacing="md" {...props} />;
  }
);

OrDivider.displayName = 'OrDivider';

export default Divider;
